import { DetailBar, Group, Modal, PhoneInput, RadioButtons, SelectInput, TextInput, Title } from "components/common"
import {
  useLazyGetProjectsQuery,
  useLazyGetSourceQuery,
  useLazyGetTypesQuery,
  useLazyGetUsersQuery,
} from "features/shared/api"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react"
import { Form } from "antd"
import { classifications } from "features/shared/data"
import { schemaRules } from "features/shared/utils"
import getLeadInitialValues from "./leadInitialValues"
import getLeadValidationSchema from "./leadValidationSchema"
import { genericSelectionType, leadTypes } from "constants/list"
import { useTranslation } from "react-i18next"
import { localisedValue, requiredValidator } from "utils"
import parentApi from "store/parentApi"
import { useSelector } from "react-redux"

const AddLeadForm = forwardRef(({ client, clientFormValues, isNewClient = false, onSubmit, preserve = false }, ref) => {
  const { t } = useTranslation()
  const loggedInUser = useSelector(state => state.app.user.info)
  const [getProjects, { data: projectsData, isLoading: projectsLoading }] = useLazyGetProjectsQuery()
  const [getTypes, { data: typesData, isLoading: typesLoading }] = useLazyGetTypesQuery()
  const [getSource, { data: sourceData, isLoading: sourceLoading, isFetching: fetchingSources }] =
    useLazyGetSourceQuery()

  const [getDepartmentUsers, { data: usersList, isFetching: isFetchingUsers }] = useLazyGetUsersQuery()
  const [getUser, { data: userData }] = parentApi.useLazyGetUserDetailQuery()
  const [form] = Form.useForm()
  const propertyTypeId = Form.useWatch("propertyType", form)
  const source = Form.useWatch("source", form)
  const previousInteraction = Form.useWatch("previousInteraction", form)
  const isClientReferred = Form.useWatch("isClientReferred", form)
  const leadType = Form.useWatch("leadType", form)
  const rules = schemaRules(getLeadValidationSchema())
  useImperativeHandle(
    ref,
    () => ({
      ...form,
    }),
    []
  )

  useEffect(() => {
    getProjects({ status: "on" })
    getTypes()
    getSource()
    getUser()
  }, [])

  useEffect(() => {
    if (
      userData?.SalesEventUsers?.length &&
      sourceData &&
      client?.referralType !== "affiliate" &&
      clientFormValues?.clientClass !== "affiliate"
    )
      form.setFieldsValue({ source: sourceData?.find(item => item?.key === "sales_event") })
  }, [userData, sourceData])

  const clientDetail = useMemo(
    () =>
      client?.id && [
        { label: t("Client Name"), value: client?.name },
        { label: t("Client ID"), value: client?.id },
        { label: t("Allocated To"), value: client?.User?.name },
      ],
    [client]
  )

  const filteredSourceData = useMemo(() => {
    return client?.referralType === "affiliate" || clientFormValues?.clientClass === "affiliate"
      ? sourceData?.filter(option => option.key === "affiliate")
      : sourceData
  }, [client, clientFormValues, sourceData])

  const affiliateBDMUser =
    (client?.clientClass === "affiliate" && client?.searchAffiliate?.user) ||
    (clientFormValues?.clientClass === "affiliate" && clientFormValues?.searchAffiliate?.user)
      ? client?.searchAffiliate?.user || clientFormValues?.searchAffiliate?.user
      : null

  const affiliateBDMOption = useMemo(
    () =>
      affiliateBDMUser
        ? {
            id: affiliateBDMUser.id,
            label: localisedValue({ object: affiliateBDMUser, key: "name" }) || affiliateBDMUser?.name,
            value: String(affiliateBDMUser.id),
          }
        : null,
    [affiliateBDMUser]
  )

  const allocatedToOptions = affiliateBDMOption ? [affiliateBDMOption] : usersList
  const shouldLockSourceAsPersonal = leadType === "investment" && source?.key === "personal"

  const flatUsersOptions = useMemo(() => {
    return usersList?.flatMap(team => team?.options || [])
  }, [usersList])

  useEffect(() => {
    if (!affiliateBDMOption && usersList && !client?.id && leadType) {
      const loggedInUserOption = flatUsersOptions?.find(option => option.id === loggedInUser?.id)
      loggedInUserOption
        ? form.setFieldsValue({ allocatedTo: loggedInUserOption })
        : form.setFieldsValue({ allocatedTo: null })
    }
  }, [affiliateBDMOption, usersList, leadType])

  // const subTypes = useMemo(() => typesData?.find(type => type.id == propertyTypeId)?.children, [propertyTypeId])
  return (
    <Group>
      {clientDetail && (
        <div>
          <Title type="secondary" level={4}>
            {t("Client Details")}
          </Title>
          <DetailBar detailData={clientDetail} />
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        name="add-lead-form"
        initialValues={getLeadInitialValues()}
        onFinish={values => onSubmit(values, { isNewClient })}
        preserve={preserve}
        colon={false}
      >
        <Group template="repeat(2, 1fr)" gap="6px 32px">
          <Form.Item name="leadType" label={t("Lead Type")} required rules={rules}>
            <SelectInput
              options={leadTypes()}
              placeholder={t("Select Lead Type")}
              onChange={value => {
                const isInvestment = value === "investment"
                const getSource = () => {
                  if (isInvestment && sourceData?.length) {
                    return sourceData.find(item => item?.key === "personal") || null
                  }

                  if (userData?.SalesEventUsers?.length) {
                    return sourceData?.find(item => item?.key === "sales_event") || null
                  }

                  return null
                }
                form.setFieldsValue({
                  leadType: value,
                  allocatedTo: null,
                  project: null,
                  classification: isInvestment ? null : form.getFieldValue("classification"),
                  propertyType: isInvestment ? null : form.getFieldValue("propertyType"),
                  subType: null,
                  source: getSource(),
                })
                !client?.id &&
                  getDepartmentUsers(
                    {
                      teamType: value !== "investment" ? "sales" : "wealth_management",
                      module: "clients",
                      subModule: "changeLeadAllocation",
                    },
                    true
                  )
              }}
            />
          </Form.Item>
          <Form.Item
            name="project"
            label={leadType !== "investment" ? t("Project") : t("Interest")}
            required={leadType !== "mortgage" && leadType !== "investment"}
            rules={leadType !== "mortgage" && leadType !== "investment" ? [requiredValidator("Project")] : []}
          >
            <SelectInput
              placeholder={leadType !== "investment" ? t("Select project") : t("Select interest")}
              options={projectsData}
              onChange={(_value, option) => form.setFieldsValue({ project: option })}
              loading={projectsLoading}
              getPopupContainer={false}
              allowClear
            />
          </Form.Item>

          {!client?.id && (
            <Form.Item name="allocatedTo" label={t("Allocated To")} required rules={rules}>
              <SelectInput
                placeholder={t("Select staff")}
                options={allocatedToOptions}
                inputLoading={isFetchingUsers}
                onChange={(_value, option) => {
                  form.setFieldsValue({ allocatedTo: option })
                }}
              />
            </Form.Item>
          )}

          {leadType !== "investment" && (
            <>
              <Form.Item name="classification" label={t("Classification")} rules={rules} required>
                <RadioButtons
                  options={Object.keys(classifications()).map(key => classifications()[key])}
                  loading={typesLoading}
                />
              </Form.Item>
              <Form.Item name="propertyType" label={t("Property Type")} rules={rules} required>
                <RadioButtons options={typesData} onChange={() => form.setFieldsValue({ subType: null })} />
              </Form.Item>
              <Form.Item name="subType" label={t("Sub Type")} rules={[requiredValidator("Sub Type")]} required>
                <SelectInput
                  placeholder={t("Select sub type")}
                  options={typesData?.find(type => type.id == propertyTypeId)?.children}
                  disabled={!propertyTypeId}
                  onChange={(_value, option) => form.setFieldsValue({ subType: option })}
                  getPopupContainer={false}
                  allowClear
                />
              </Form.Item>
            </>
          )}
          <Form.Item name="source" label={t("Please select a source")} rules={rules} required>
            <SelectInput
              placeholder={t("Source")}
              options={filteredSourceData}
              inputLoading={fetchingSources}
              onChange={(_value, option) => form.setFieldsValue({ source: option })}
              loading={sourceLoading}
              getPopupContainer={false}
              disabled={
                !!userData?.SalesEventUsers?.length &&
                client?.referralType !== "affiliate" &&
                clientFormValues?.clientClass !== "affiliate"
              }
            />
          </Form.Item>
          {!!userData?.SalesEventUsers?.length && source?.key === "sales_event" && (
            <Form.Item name="salesEvent" label={t("Sales Event")} required rules={rules}>
              <SelectInput placeholder={t("Select sales event")} options={userData?.SalesEventUsers} />
            </Form.Item>
          )}
          {client === undefined && (
            <>
              {source?.key === "personal" && (
                <Form.Item
                  name="referralSource"
                  label={t("How did you come to know this client?")}
                  rules={leadType !== "investment" ? rules : []} 
                  required={leadType !== "investment"}           
                >
                  <TextInput placeholder={t("Please type here")} />
                </Form.Item>
              )}
              {source?.key === "personal" && leadType !== "investment" && (
                <Form.Item
                  name="previousInteraction"
                  label={t("Have you interacted with this client before using a different number?")}
                  rules={rules}
                  required
                >
                  <RadioButtons options={genericSelectionType()} />
                </Form.Item>
              )}
              {previousInteraction === "true" && leadType !== "investment" && (
                <Form.Item
                  name="previousInteractionNumber"
                  label={t("Please enter the phone number used for client interactions")}
                  rules={rules}
                  required
                  hasFeedback
                  style={{ flexBasis: "48ch" }}
                >
                  <PhoneInput />
                </Form.Item>
              )}
              {source?.key === "personal" && leadType!=="investment" && (
                <Form.Item
                  name="isClientReferred"
                  label={t("Was this client referred to you by someone?")}
                  rules={rules}
                  required
                >
                  <RadioButtons options={genericSelectionType()} />
                </Form.Item>
              )}
              {isClientReferred === "true" && (
                <Form.Item
                  name="referralDetails"
                  label={t("What is your relationship with the referrer?")}
                  rules={rules}
                  required
                >
                  <TextInput placeholder={t("Relationship with the referrer")} />
                </Form.Item>
              )}
            </>
          )}
        </Group>
      </Form>
    </Group>
  )
})

const AddLeadFormModal = ({
  modalVisible,
  setModalVisible,
  client,
  onSubmit,
  loading,
  onModalVisible = () => {},
  footerAlert,
}) => {
  const { t } = useTranslation()
  const formRef = useRef()
  return (
    <Modal
      title={t("Add Lead")}
      open={modalVisible}
      width={900}
      okText={t("Okay")}
      cancelText={t("Cancel")}
      onOk={() => formRef.current?.submit()}
      loading={loading}
      onModalVisible={onModalVisible}
      onCancel={() => setModalVisible(false)}
      footerAlert={footerAlert}
      destroyOnClose
    >
      {modalVisible && client && <AddLeadForm ref={formRef} client={client} onSubmit={onSubmit} />}
    </Modal>
  )
}

export { AddLeadForm, AddLeadFormModal }
